import {
  isUuid,
  type AnnotationRecord,
  type IdFactory,
} from "@/domain";

import type { MarginaliaAnnotationV1 } from "./schemas/v1";

export type AnnotationMergeCounts = {
  created: number;
  updated: number;
  skipped: number;
  conflicts: number;
  deletions: number;
};

export type AnnotationMergeAction =
  | {
      kind: "insert" | "update" | "delete";
      annotationId: string;
      record: AnnotationRecord;
    }
  | {
      kind: "conflict";
      annotationId: string;
      conflictId: string;
      record: AnnotationRecord;
    }
  | {
      kind: "skip";
      annotationId: string;
      reason: "identical" | "local-newer";
    };

export type AnnotationMergeResult = {
  counts: AnnotationMergeCounts;
  actions: AnnotationMergeAction[];
  recordsToPut: AnnotationRecord[];
};

export type MergeAnnotationsOptions = {
  createId: IdFactory;
  occupiedIds?: Iterable<string>;
  foreignAnnotationIds?: ReadonlySet<string>;
};

export function annotationRecordToExport(
  annotation: AnnotationRecord,
): MarginaliaAnnotationV1 {
  return {
    id: annotation.id,
    pageIndex: annotation.pageIndex,
    kind: annotation.kind,
    target: {
      text: annotation.target.text,
      geometry: annotation.target.geometry,
    },
    body: annotation.body,
    color: annotation.color,
    preferredSide: annotation.preferredSide,
    tags: annotation.tags,
    conflictOf: annotation.conflictOf,
    revision: annotation.revision,
    createdAt: annotation.createdAt,
    updatedAt: annotation.updatedAt,
    deletedAt: annotation.deletedAt,
  };
}

export function exportAnnotationToRecord(
  annotation: MarginaliaAnnotationV1,
  documentId: string,
): AnnotationRecord {
  return {
    schemaVersion: 1,
    id: annotation.id,
    documentId,
    pageIndex: annotation.pageIndex,
    kind: annotation.kind,
    target: {
      pageIndex: annotation.pageIndex,
      text: annotation.target.text,
      geometry: annotation.target.geometry,
    },
    body: annotation.body,
    color: annotation.color,
    preferredSide: annotation.preferredSide,
    tags: [...annotation.tags],
    conflictOf: annotation.conflictOf,
    revision: annotation.revision,
    createdAt: annotation.createdAt,
    updatedAt: annotation.updatedAt,
    deletedAt: annotation.deletedAt,
  };
}

function annotationsAreIdentical(
  local: AnnotationRecord,
  incoming: MarginaliaAnnotationV1,
): boolean {
  return stableStringify(annotationRecordToExport(local)) === stableStringify(incoming);
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  if (typeof value === "object" && value !== null) {
    const entries = Object.entries(value).sort(([left], [right]) =>
      left < right ? -1 : left > right ? 1 : 0,
    );
    return `{${entries
      .map(
        ([key, entryValue]) =>
          `${JSON.stringify(key)}:${stableStringify(entryValue)}`,
      )
      .join(",")}}`;
  }

  return JSON.stringify(value);
}

function createUniqueConflictId(
  createId: IdFactory,
  occupiedIds: Set<string>,
): string {
  for (let attempt = 0; attempt < 100; attempt += 1) {
    const id = createId();
    if (!isUuid(id)) {
      throw new TypeError("Conflict annotation ID factory must return a UUID.");
    }
    if (!occupiedIds.has(id)) {
      occupiedIds.add(id);
      return id;
    }
  }

  throw new Error("Could not generate a unique conflict annotation ID.");
}

export function mergeAnnotations(
  localAnnotations: readonly AnnotationRecord[],
  incomingAnnotations: readonly MarginaliaAnnotationV1[],
  documentId: string,
  options: MergeAnnotationsOptions,
): AnnotationMergeResult {
  const localById = new Map(
    localAnnotations.map((annotation) => [annotation.id, annotation]),
  );
  const occupiedIds = new Set(options.occupiedIds ?? localById.keys());
  for (const id of localById.keys()) {
    occupiedIds.add(id);
  }
  for (const annotation of incomingAnnotations) {
    occupiedIds.add(annotation.id);
  }

  const counts: AnnotationMergeCounts = {
    created: 0,
    updated: 0,
    skipped: 0,
    conflicts: 0,
    deletions: 0,
  };
  const actions: AnnotationMergeAction[] = [];
  const recordsToPut: AnnotationRecord[] = [];

  for (const incoming of incomingAnnotations) {
    if (options.foreignAnnotationIds?.has(incoming.id)) {
      const conflictId = createUniqueConflictId(options.createId, occupiedIds);
      const conflictRecord: AnnotationRecord = {
        ...exportAnnotationToRecord(incoming, documentId),
        id: conflictId,
        conflictOf: incoming.id,
        revision: 1,
      };
      counts.conflicts += 1;
      actions.push({
        kind: "conflict",
        annotationId: incoming.id,
        conflictId,
        record: conflictRecord,
      });
      recordsToPut.push(conflictRecord);
      localById.set(conflictId, conflictRecord);
      continue;
    }

    const local = localById.get(incoming.id);
    const incomingRecord = exportAnnotationToRecord(incoming, documentId);

    if (local === undefined) {
      const kind = incoming.deletedAt === null ? "insert" : "delete";
      if (kind === "insert") {
        counts.created += 1;
      } else {
        counts.deletions += 1;
      }
      actions.push({
        kind,
        annotationId: incoming.id,
        record: incomingRecord,
      });
      recordsToPut.push(incomingRecord);
      localById.set(incoming.id, incomingRecord);
      continue;
    }

    if (annotationsAreIdentical(local, incoming)) {
      counts.skipped += 1;
      actions.push({
        kind: "skip",
        annotationId: incoming.id,
        reason: "identical",
      });
      continue;
    }

    if (
      incoming.deletedAt !== null &&
      incoming.revision >= local.revision
    ) {
      counts.deletions += 1;
      actions.push({
        kind: "delete",
        annotationId: incoming.id,
        record: incomingRecord,
      });
      recordsToPut.push(incomingRecord);
      localById.set(incoming.id, incomingRecord);
      continue;
    }

    if (incoming.revision > local.revision) {
      counts.updated += 1;
      actions.push({
        kind: "update",
        annotationId: incoming.id,
        record: incomingRecord,
      });
      recordsToPut.push(incomingRecord);
      localById.set(incoming.id, incomingRecord);
      continue;
    }

    if (local.revision > incoming.revision) {
      counts.skipped += 1;
      actions.push({
        kind: "skip",
        annotationId: incoming.id,
        reason: "local-newer",
      });
      continue;
    }

    const conflictId = createUniqueConflictId(options.createId, occupiedIds);
    const conflictRecord: AnnotationRecord = {
      ...incomingRecord,
      id: conflictId,
      conflictOf: incoming.id,
      revision: 1,
    };
    counts.conflicts += 1;
    actions.push({
      kind: "conflict",
      annotationId: incoming.id,
      conflictId,
      record: conflictRecord,
    });
    recordsToPut.push(conflictRecord);
    localById.set(conflictId, conflictRecord);
  }

  return { counts, actions, recordsToPut };
}
